// /admin/empresas/:id — detalle de una empresa B2B.
// 5 tabs: Info · Direcciones · Presupuestos · Interacciones · Pedidos.
// Cada tab tiene CRUD propio.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import MapPin from '../../components/MapPin'
import { supabase } from '../../lib/supabase'
import { geocodificar, validarBarrioConDireccion } from '../../lib/geo'

const PIPELINE = [
  { v: 'prospect',   l: 'Prospect',   color: '#0EA5E9', bg: '#E0F2FE' },
  { v: 'cotizando',  l: 'Cotizando',  color: '#CA8A04', bg: '#FEF9C3' },
  { v: 'negociando', l: 'Negociando', color: '#7C3AED', bg: '#EDE9FE' },
  { v: 'activo',     l: 'Activo',     color: '#16A34A', bg: '#DCFCE7' },
  { v: 'pausado',    l: 'Pausado',    color: '#64748B', bg: '#F1F5F9' },
  { v: 'perdido',    l: 'Perdido',    color: '#DC2626', bg: '#FEE2E2' },
]

const TIPO_LABEL = {
  comercio: 'Comercio',
  oficina: 'Oficina',
  administradora: 'Administradora de edificios',
  otro: 'Otro',
}

const FACT_MODO_LABEL = {
  una_por_edificio: 'Una e-Factura por edificio (default)',
  consolidada: 'Una e-Factura consolidada a la razón social',
}

const TABS = [
  { v: 'info',           l: 'Info',           icon: '📋' },
  { v: 'direcciones',    l: 'Direcciones',    icon: '📍' },
  { v: 'edificios',      l: 'Edificios',      icon: '🏢', soloAdmin: true },
  { v: 'presupuestos',   l: 'Presupuestos',   icon: '📄' },
  { v: 'servicio',       l: 'Servicio',       icon: '🔄' },
  { v: 'facturas',       l: 'Facturas',       icon: '🧾' },
  { v: 'regalos',        l: 'Regalos',        icon: '🎁' },
  { v: 'interacciones',  l: 'Interacciones',  icon: '💬' },
]

export default function EmpresaDetalle() {
  const { id } = useParams()
  const nav = useNavigate()
  const [empresa, setEmpresa] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState('info')

  async function cargar() {
    setCargando(true)
    setError(null)
    const { data, error: e } = await supabase
      .from('empresas')
      .select(`
        *,
        owner:profiles!empresas_owner_id_fkey(id, nombre),
        creador:profiles!empresas_created_by_fkey(id, nombre),
        consulta:consultas_comerciales(id, nombre, empresa, email, telefono, mensaje, created_at)
      `)
      .eq('id', id)
      .single()
    if (e) setError(e.message)
    else setEmpresa(data)
    setCargando(false)
  }

  useEffect(() => { cargar() }, [id])

  if (cargando) {
    return (
      <AdminLayout title="Empresa">
        <div style={{ padding: 40, textAlign: 'center', color: '#64748B' }}>Cargando…</div>
      </AdminLayout>
    )
  }

  if (error || !empresa) {
    return (
      <AdminLayout title="Empresa">
        <div style={{ padding: 24 }}>
          <button onClick={() => nav('/admin/empresas')} style={{ marginBottom: 16 }}>← Volver</button>
          <div style={{ background: '#FEE2E2', padding: 16, borderRadius: 8, color: '#991B1B' }}>
            {error || 'Empresa no encontrada'}
          </div>
        </div>
      </AdminLayout>
    )
  }

  const estado = PIPELINE.find(p => p.v === empresa.estado_pipeline) || PIPELINE[0]

  async function archivar() {
    const motivo = prompt('Motivo del archivado (opcional):', '')
    if (motivo === null) return
    if (!confirm(`Archivar "${empresa.razon_social}"? Queda fuera de las listas pero se preserva todo el historial. Podés desarchivarla después.`)) return
    const { data: { user } } = await supabase.auth.getUser()
    const { error: e } = await supabase.from('empresas').update({
      archivada_at: new Date().toISOString(),
      archivada_por: user?.id || null,
      archivada_motivo: motivo || null,
    }).eq('id', empresa.id)
    if (e) alert('Error: ' + e.message)
    else nav('/admin/empresas')
  }

  async function desarchivar() {
    if (!confirm(`Desarchivar "${empresa.razon_social}"? Vuelve a aparecer en las listas.`)) return
    const { error: e } = await supabase.from('empresas').update({
      archivada_at: null,
      archivada_por: null,
      archivada_motivo: null,
    }).eq('id', empresa.id)
    if (e) alert('Error: ' + e.message)
    else cargar()
  }

  async function eliminarDefinitivo() {
    // Verificar que NO tenga nada vinculado
    const [{ count: presups }, { count: subs }, { count: peds }, { count: facts }, { count: dirs }, { count: ints }] = await Promise.all([
      supabase.from('presupuestos').select('id', { count: 'exact', head: true }).eq('empresa_id', empresa.id),
      supabase.from('suscripciones_empresa').select('id', { count: 'exact', head: true }).eq('empresa_id', empresa.id),
      supabase.from('pedidos').select('id', { count: 'exact', head: true }).eq('empresa_id', empresa.id),
      supabase.from('facturas_emitidas').select('id', { count: 'exact', head: true }).eq('empresa_id', empresa.id),
      supabase.from('empresas_direcciones').select('id', { count: 'exact', head: true }).eq('empresa_id', empresa.id),
      supabase.from('empresas_interacciones').select('id', { count: 'exact', head: true }).eq('empresa_id', empresa.id),
    ])
    const partes = []
    if (presups > 0) partes.push(`${presups} presupuesto(s)`)
    if (subs > 0) partes.push(`${subs} suscripción(es)`)
    if (peds > 0) partes.push(`${peds} pedido(s)`)
    if (facts > 0) partes.push(`${facts} factura(s)`)
    if (dirs > 0) partes.push(`${dirs} dirección(es)`)
    if (ints > 0) partes.push(`${ints} interacción(es)`)
    if (partes.length > 0 && (presups > 0 || subs > 0 || peds > 0 || facts > 0)) {
      alert(`No se puede eliminar definitivamente: tiene ${partes.join(', ')}. Usá "Archivar" en su lugar.`)
      return
    }
    const detalleHist = partes.length > 0 ? `\n\nVa a borrar también: ${partes.join(', ')}.` : ''
    if (!confirm(`⚠ ELIMINAR DEFINITIVAMENTE a "${empresa.razon_social}"? Esto NO se puede deshacer.${detalleHist}`)) return
    // Borrar interacciones y direcciones primero (FK on delete restrict en algunas)
    if (ints > 0) await supabase.from('empresas_interacciones').delete().eq('empresa_id', empresa.id)
    if (dirs > 0) await supabase.from('empresas_direcciones').delete().eq('empresa_id', empresa.id)
    const { error: e } = await supabase.from('empresas').delete().eq('id', empresa.id)
    if (e) alert('Error: ' + e.message)
    else nav('/admin/empresas')
  }

  return (
    <AdminLayout title={empresa.razon_social}>
      <Estilos />
      <div className="ed-page">
        <button className="ed-back" onClick={() => nav('/admin/empresas')}>← Volver a empresas</button>

        {empresa.archivada_at && (
          <div className="ed-archivada-banner">
            <strong>📦 Empresa archivada</strong> el {formatFecha(empresa.archivada_at)}.
            {empresa.archivada_motivo && <> Motivo: {empresa.archivada_motivo}.</>}
            <button className="ed-btn-link" onClick={desarchivar}>↻ Desarchivar</button>
          </div>
        )}

        <header className="ed-head">
          <div>
            <h1>{empresa.razon_social}</h1>
            <div className="ed-meta">
              <span>{TIPO_LABEL[empresa.tipo]}</span>
              {empresa.rut && <span>· RUT {empresa.rut}</span>}
              {empresa.owner && <span>· Owner: {empresa.owner.nombre}</span>}
            </div>
          </div>
          <div className="ed-head-right">
            <span className="ed-badge" style={{ background: estado.bg, color: estado.color }}>{estado.l}</span>
            {!empresa.archivada_at && (
              <div className="ed-head-menu">
                <button className="ed-btn-icon" onClick={archivar} title="Archivar (queda fuera de listas, preserva historial)">📦</button>
                <button className="ed-btn-icon ed-btn-danger" onClick={eliminarDefinitivo} title="Eliminar definitivo (solo si no tiene historial)">🗑</button>
              </div>
            )}
          </div>
        </header>

        <nav className="ed-tabs">
          {TABS.filter(t => !t.soloAdmin || empresa.tipo === 'administradora').map(t => (
            <button
              key={t.v}
              className={`ed-tab${tab === t.v ? ' active' : ''}`}
              onClick={() => setTab(t.v)}
            >
              <span className="ed-tab-icon">{t.icon}</span> {t.l}
            </button>
          ))}
        </nav>

        <div className="ed-content">
          {tab === 'info'         && <TabInfo empresa={empresa} onUpdated={cargar} />}
          {tab === 'direcciones'  && <TabDirecciones empresaId={empresa.id} />}
          {tab === 'presupuestos' && <TabPresupuestos empresaId={empresa.id} />}
          {tab === 'servicio'     && <TabServicio empresaId={empresa.id} />}
          {tab === 'facturas'     && <TabFacturas empresa={empresa} />}
          {tab === 'regalos'      && <TabRegalos empresaId={empresa.id} empresaNombre={empresa.razon_social} />}
          {tab === 'interacciones'&& <TabInteracciones empresaId={empresa.id} />}
          {tab === 'edificios'    && <TabEdificios empresa={empresa} />}
        </div>
      </div>
    </AdminLayout>
  )
}

// ─────────────────────────────────────────────────────────
// Tab 1: Info (datos generales editables + estado pipeline)
// ─────────────────────────────────────────────────────────
function TabInfo({ empresa, onUpdated }) {
  const [form, setForm] = useState({
    razon_social: empresa.razon_social || '',
    nombre_fantasia: empresa.nombre_fantasia || '',
    rut: empresa.rut || '',
    tipo: empresa.tipo,
    estado_pipeline: empresa.estado_pipeline,
    facturacion_modo: empresa.facturacion_modo,
    email_facturacion: empresa.email_facturacion || '',
    telefono: empresa.telefono || '',
    contacto_principal_nombre: empresa.contacto_principal_nombre || '',
    contacto_principal_cargo: empresa.contacto_principal_cargo || '',
    notas: empresa.notas || '',
  })
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)
  const [ok, setOk] = useState(false)

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function guardar() {
    setError(null)
    setOk(false)
    setGuardando(true)
    const { error: e } = await supabase
      .from('empresas')
      .update({
        ...form,
        nombre_fantasia: form.nombre_fantasia.trim() || null,
        rut: form.rut.trim() || null,
        email_facturacion: form.email_facturacion.trim() || null,
        telefono: form.telefono.trim() || null,
        contacto_principal_nombre: form.contacto_principal_nombre.trim() || null,
        contacto_principal_cargo: form.contacto_principal_cargo.trim() || null,
        notas: form.notas.trim() || null,
      })
      .eq('id', empresa.id)
    setGuardando(false)
    if (e) setError(e.message)
    else {
      setOk(true)
      onUpdated()
      setTimeout(() => setOk(false), 2500)
    }
  }

  return (
    <div className="ed-card">
      <div className="ed-grid-2">
        <Field label="Razón social * (para CFE)">
          <input value={form.razon_social} onChange={e => setF('razon_social', e.target.value)} placeholder="Ej: Camitol SAS" />
        </Field>
        <Field label="RUT">
          <input value={form.rut} onChange={e => setF('rut', e.target.value)} placeholder="Opcional" />
        </Field>
      </div>

      <Field label="Nombre fantasía / comercial (visible en etiquetas y comunicaciones)">
        <input
          value={form.nombre_fantasia}
          onChange={e => setF('nombre_fantasia', e.target.value)}
          placeholder="Ej: Propics — opcional. Si está, aparece arriba en etiquetas y cards. El CFE sigue usando razón social."
        />
      </Field>

      <div className="ed-grid-2">
        <Field label="Tipo">
          <select value={form.tipo} onChange={e => setF('tipo', e.target.value)}>
            {Object.entries(TIPO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Estado pipeline">
          <select value={form.estado_pipeline} onChange={e => setF('estado_pipeline', e.target.value)}>
            {PIPELINE.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Modo de facturación">
        <select value={form.facturacion_modo} onChange={e => setF('facturacion_modo', e.target.value)}>
          {Object.entries(FACT_MODO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>

      <div className="ed-grid-2">
        <Field label="Contacto principal — nombre">
          <input value={form.contacto_principal_nombre} onChange={e => setF('contacto_principal_nombre', e.target.value)} />
        </Field>
        <Field label="Contacto principal — cargo">
          <input value={form.contacto_principal_cargo} onChange={e => setF('contacto_principal_cargo', e.target.value)} placeholder="Gerente, encargado, dueño..." />
        </Field>
      </div>

      <div className="ed-grid-2">
        <Field label="Teléfono">
          <input value={form.telefono} onChange={e => setF('telefono', e.target.value)} />
        </Field>
        <Field label="Email facturación">
          <input value={form.email_facturacion} onChange={e => setF('email_facturacion', e.target.value)} />
        </Field>
      </div>

      <Field label="Notas internas (no se muestran al cliente)">
        <textarea rows={4} value={form.notas} onChange={e => setF('notas', e.target.value)} />
      </Field>

      {empresa.consulta && (
        <div className="ed-callout">
          <strong>📥 Lead de origen:</strong> {empresa.consulta.empresa} — {empresa.consulta.nombre}
          {empresa.consulta.created_at && <> ({new Date(empresa.consulta.created_at).toLocaleDateString('es-UY')})</>}
          <div className="ed-callout-msg">{empresa.consulta.mensaje}</div>
        </div>
      )}

      {error && <div className="ed-error">⚠ {error}</div>}
      {ok && <div className="ed-ok">✓ Guardado</div>}

      <div className="ed-actions">
        <button className="ed-btn-primary" onClick={guardar} disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// Tab 2: Direcciones (CRUD)
// ─────────────────────────────────────────────────────────
function TabDirecciones({ empresaId }) {
  const [direcciones, setDirecciones] = useState([])
  const [barrios, setBarrios] = useState([])
  const [cargando, setCargando] = useState(true)
  const [modal, setModal] = useState(null) // null | {id?, alias, ...}
  const [error, setError] = useState(null)

  async function cargar() {
    setCargando(true)
    const [{ data: dirs, error: e1 }, { data: bs, error: e2 }] = await Promise.all([
      supabase.from('empresas_direcciones').select('*, barrio:barrios(id, nombre, dias)').eq('empresa_id', empresaId).order('created_at'),
      supabase.from('barrios').select('id, nombre, dias').eq('activo', true).order('nombre'),
    ])
    if (e1 || e2) setError((e1 || e2).message)
    setDirecciones(dirs || [])
    setBarrios(bs || [])
    setCargando(false)
  }

  useEffect(() => { cargar() }, [empresaId])

  async function guardarDir(form) {
    const payload = {
      empresa_id: empresaId,
      alias: form.alias.trim(),
      calle: form.calle.trim(),
      numero: form.numero.trim() || null,
      apto_unidad: form.apto_unidad.trim() || null,
      // Fuera de zona: decisión explícita del superadmin (entrega coordinada aparte). Si está
      // marcada, el barrio queda null a propósito — ya no es un "sin barrio" silencioso.
      fuera_de_zona: !!form.fuera_de_zona,
      barrio_id: form.fuera_de_zona ? null : (form.barrio_id || null),
      contacto_local_nombre: form.contacto_local_nombre.trim() || null,
      contacto_local_telefono: form.contacto_local_telefono.trim() || null,
      observaciones: form.observaciones.trim() || null,
      activa: form.activa !== false,
      // Coordenadas (vienen del geocoding o del pin draggable del modal)
      lat: form.lat || null,
      lng: form.lng || null,
      geocoded_at: (form.lat && form.lng) ? new Date().toISOString() : null,
    }
    const { error: e } = form.id
      ? await supabase.from('empresas_direcciones').update(payload).eq('id', form.id)
      : await supabase.from('empresas_direcciones').insert(payload)
    if (e) {
      setError(e.message)
      return false
    }
    setError(null)
    setModal(null)
    await cargar()
    return true
  }

  async function eliminar(id) {
    if (!confirm('¿Eliminar esta dirección?')) return
    const { error: e } = await supabase.from('empresas_direcciones').delete().eq('id', id)
    if (e) setError(e.message)
    else cargar()
  }

  return (
    <div>
      <div className="ed-toolbar">
        <button className="ed-btn-primary" onClick={() => setModal({})}>+ Nueva dirección</button>
      </div>

      {error && <div className="ed-error">⚠ {error}</div>}

      {cargando ? (
        <div className="ed-loading-thin">Cargando…</div>
      ) : direcciones.length === 0 ? (
        <div className="ed-empty">
          <p>Esta empresa todavía no tiene direcciones cargadas.</p>
          <p className="ed-empty-sub">Las administradoras suelen tener N edificios — los comercios típicamente 1.</p>
        </div>
      ) : (
        <div className="ed-dir-grid">
          {direcciones.map(d => (
            <div key={d.id} className="ed-dir-card">
              <div className="ed-dir-head">
                <strong>{d.alias}</strong>
                {!d.activa && <span className="ed-pill-muted">pausada</span>}
                {d.lat && d.lng
                  ? <span className="ed-pill-geo-ok" title="Geocodificada">📍</span>
                  : <span className="ed-pill-geo-miss" title="Sin geocodificar — editá para sumar mapa">⚠ sin mapa</span>}
              </div>
              <div className="ed-dir-addr">{d.calle} {d.numero || ''}{d.apto_unidad ? ` · Apto ${d.apto_unidad}` : ''}</div>
              <div className="ed-dir-meta">
                {d.fuera_de_zona
                  ? <span className="ed-pill-fz" title="Fuera de la ruta de barrios — entrega coordinada aparte">🚚 Fuera de zona</span>
                  : (d.barrio?.nombre || <span className="ed-pill-sin-barrio" title="Sin barrio y sin marcar fuera de zona — editá para definirlo">⚠ Sin barrio</span>)}
              </div>
              {d.contacto_local_nombre && (
                <div className="ed-dir-contact">
                  👤 {d.contacto_local_nombre} {d.contacto_local_telefono && `· ${d.contacto_local_telefono}`}
                </div>
              )}
              {d.observaciones && <div className="ed-dir-obs">{d.observaciones}</div>}
              <div className="ed-dir-actions">
                <button className="ed-btn-link" onClick={() => setModal(d)}>Editar</button>
                <button className="ed-btn-link ed-btn-danger" onClick={() => eliminar(d.id)}>Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && <ModalDireccion direccion={modal} barrios={barrios} onSave={guardarDir} onClose={() => setModal(null)} />}
    </div>
  )
}

function ModalDireccion({ direccion, barrios, onSave, onClose }) {
  const [form, setForm] = useState({
    id: direccion.id,
    alias: direccion.alias || '',
    calle: direccion.calle || '',
    numero: direccion.numero || '',
    apto_unidad: direccion.apto_unidad || '',
    barrio_id: direccion.barrio_id || '',
    fuera_de_zona: !!direccion.fuera_de_zona,
    contacto_local_nombre: direccion.contacto_local_nombre || '',
    contacto_local_telefono: direccion.contacto_local_telefono || '',
    observaciones: direccion.observaciones || '',
    activa: direccion.activa !== false,
    lat: direccion.lat || null,
    lng: direccion.lng || null,
  })
  const [guardando, setGuardando] = useState(false)
  const [geocoding, setGeocoding] = useState(false)
  const [geoMsg, setGeoMsg] = useState(null)  // {tipo: 'ok'|'warn'|'error', texto}

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }

  // Geocodificar la dirección actual
  async function geocodificarAhora() {
    if (!form.calle.trim()) {
      setGeoMsg({ tipo: 'error', texto: 'Cargá la calle primero' })
      return
    }
    setGeocoding(true)
    setGeoMsg(null)
    try {
      const direccionTexto = `${form.calle} ${form.numero || ''}`.trim()
      const res = await geocodificar(direccionTexto)
      if (!res) {
        setGeoMsg({ tipo: 'error', texto: 'No pudimos encontrar la dirección en el mapa. Cargá las coordenadas a mano arrastrando el pin.' })
        return
      }
      // Setear coords
      setF('lat', res.lat)
      setF('lng', res.lng)
      // Validar contra el barrio elegido si hay
      if (form.barrio_id) {
        const barrioElegido = barrios.find(b => b.id === form.barrio_id)
        const v = validarBarrioConDireccion(res.barrioDetectado, barrioElegido?.nombre, barrios)
        if (v.ok) {
          setGeoMsg({ tipo: 'ok', texto: `✓ Encontrado en el mapa · Coincide con ${barrioElegido.nombre}` })
        } else if (v.sugerenciaActivo) {
          setGeoMsg({ tipo: 'warn', texto: `⚠ El mapa dice que la dirección está en ${v.sugerenciaActivo.nombre}. ¿Cambiar el barrio?` })
        } else if (v.sinCobertura) {
          setGeoMsg({ tipo: 'warn', texto: `⚠ El mapa detecta ${res.barrioDetectado || 'un barrio'} fuera de nuestra cobertura. Verificá si vale.` })
        } else {
          setGeoMsg({ tipo: 'ok', texto: `✓ Encontrado en el mapa` })
        }
      } else {
        setGeoMsg({ tipo: 'ok', texto: `✓ Encontrado en el mapa${res.barrioDetectado ? ` · Barrio: ${res.barrioDetectado}` : ''}` })
      }
    } catch (err) {
      setGeoMsg({ tipo: 'error', texto: 'Error al geocodificar: ' + err.message })
    } finally {
      setGeocoding(false)
    }
  }

  // Auto-geocodificar al cambiar calle/numero si hay autofocus de save → mejor manual
  // El usuario clickea "📍 Buscar en mapa" o se hace al guardar si no hay coords

  async function submit() {
    if (!form.alias.trim() || !form.calle.trim()) {
      alert('Alias y calle son obligatorios')
      return
    }
    // Si no hay coords, intentar geocodificar antes de guardar
    if (!form.lat || !form.lng) {
      setGuardando(true)
      const direccionTexto = `${form.calle} ${form.numero || ''}`.trim()
      const res = await geocodificar(direccionTexto)
      if (res) {
        form.lat = res.lat
        form.lng = res.lng
      } else {
        setGuardando(false)
        if (!confirm('No pudimos encontrar la dirección en el mapa. ¿Guardar igual sin coordenadas? (después se puede ajustar)')) return
        setGuardando(true)
      }
    }
    await onSave(form)
    setGuardando(false)
  }

  return (
    <div className="emp-modal-bg" onClick={onClose}>
      <div className="emp-modal" onClick={e => e.stopPropagation()}>
        <header className="emp-modal-head">
          <h3>{direccion.id ? 'Editar dirección' : 'Nueva dirección'}</h3>
          <button className="emp-modal-close" onClick={onClose}>✕</button>
        </header>
        <div className="emp-modal-body">
          <Field label="Alias *">
            <input value={form.alias} onChange={e => setF('alias', e.target.value)} placeholder="Edificio Sarandí 612 / Local Pocitos" autoFocus />
          </Field>
          <div className="ed-grid-3">
            <Field label="Calle *">
              <input value={form.calle} onChange={e => setF('calle', e.target.value)} />
            </Field>
            <Field label="Número">
              <input value={form.numero} onChange={e => setF('numero', e.target.value)} />
            </Field>
            <Field label="Apto / Unidad">
              <input value={form.apto_unidad} onChange={e => setF('apto_unidad', e.target.value)} />
            </Field>
          </div>
          <Field label="Barrio">
            <select
              value={form.barrio_id}
              disabled={form.fuera_de_zona}
              onChange={e => setF('barrio_id', e.target.value)}
            >
              <option value="">— Seleccionar —</option>
              {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
            {/* Marcador explícito: distingue "es a propósito, está fuera de ruta" de "me olvidé
                el barrio". Solo aplica a empresas (el B2C siempre exige barrio). */}
            <label className="ed-fz-check">
              <input
                type="checkbox"
                checked={form.fuera_de_zona}
                onChange={e => {
                  const v = e.target.checked
                  setForm(f => ({ ...f, fuera_de_zona: v, barrio_id: v ? '' : f.barrio_id }))
                }}
              />
              <span>Fuera de zona de reparto — entrega coordinada aparte</span>
            </label>
            {form.fuera_de_zona && (
              <div className="ed-fz-nota">
                Esta dirección no entra en la ruta de barrios. Los pedidos B2B a esta dirección se
                permiten igual y el envío lo definís vos en cada pedido.
              </div>
            )}
            {!form.fuera_de_zona && !form.barrio_id && (
              <div className="ed-fz-warn">
                ⚠ Sin barrio y sin marcar fuera de zona. Elegí el barrio, o marcá la casilla si es a propósito.
              </div>
            )}
          </Field>
          <div className="ed-grid-2">
            <Field label="Contacto local (encargado, gerente…)">
              <input value={form.contacto_local_nombre} onChange={e => setF('contacto_local_nombre', e.target.value)} />
            </Field>
            <Field label="Teléfono contacto local">
              <input value={form.contacto_local_telefono} onChange={e => setF('contacto_local_telefono', e.target.value)} />
            </Field>
          </div>
          <Field label="Observaciones para entrega">
            <textarea rows={3} value={form.observaciones} onChange={e => setF('observaciones', e.target.value)} placeholder="Subir por ascensor de servicio, tocar timbre del fondo, etc." />
          </Field>

          {/* Mapa + geocoding */}
          <div className="ed-mapa-block">
            <div className="ed-mapa-head">
              <strong>📍 Ubicación en mapa</strong>
              <button type="button" className="ed-btn-link" onClick={geocodificarAhora} disabled={geocoding || !form.calle.trim()}>
                {geocoding ? 'Buscando…' : '🔍 Buscar en mapa'}
              </button>
            </div>
            {geoMsg && (
              <div className={`ed-mapa-msg ed-mapa-msg-${geoMsg.tipo}`}>{geoMsg.texto}</div>
            )}
            {form.lat && form.lng ? (
              <>
                <MapPin
                  lat={form.lat}
                  lng={form.lng}
                  direccion={`${form.calle} ${form.numero || ''}`.trim()}
                  onMove={(lat, lng) => { setF('lat', lat); setF('lng', lng) }}
                />
                <div className="ed-mapa-coords">
                  Coords: <code>{form.lat.toFixed(6)}, {form.lng.toFixed(6)}</code>
                  · Arrastrá el pin si no está exacto.
                </div>
              </>
            ) : (
              <div className="ed-mapa-empty">
                Sin coordenadas todavía. Click "🔍 Buscar en mapa" para geocodificar la dirección.
              </div>
            )}
          </div>

          <label className="ed-check">
            <input type="checkbox" checked={form.activa} onChange={e => setF('activa', e.target.checked)} />
            <span>Dirección activa</span>
          </label>
        </div>
        <footer className="emp-modal-foot">
          <button className="emp-btn-secondary" onClick={onClose} disabled={guardando}>Cancelar</button>
          <button className="emp-btn-primary" onClick={submit} disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</button>
        </footer>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// Tab 3: Presupuestos (lista + botón "Nuevo")
// ─────────────────────────────────────────────────────────
function TabPresupuestos({ empresaId }) {
  const nav = useNavigate()
  const [presupuestos, setPresupuestos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)

  async function cargar() {
    setCargando(true)
    const { data, error: e } = await supabase
      .from('presupuestos')
      .select('id, numero, fecha, validez_dias, estado, total, enviado_at, aceptado_at')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false })
    if (e) setError(e.message)
    setPresupuestos(data || [])
    setCargando(false)
  }

  useEffect(() => { cargar() }, [empresaId])

  return (
    <div>
      <div className="ed-toolbar">
        <button className="ed-btn-primary" onClick={() => nav(`/admin/empresas/${empresaId}/presupuesto/nuevo`)}>
          + Nuevo presupuesto
        </button>
      </div>

      {error && <div className="ed-error">⚠ {error}</div>}

      {cargando ? (
        <div className="ed-loading-thin">Cargando…</div>
      ) : presupuestos.length === 0 ? (
        <div className="ed-empty">
          <p>Sin presupuestos todavía.</p>
          <p className="ed-empty-sub">Cuando armes uno, queda acá con su número, estado y total.</p>
        </div>
      ) : (
        <table className="ed-table">
          <thead>
            <tr><th>Nº</th><th>Fecha</th><th>Validez</th><th>Estado</th><th>Total</th><th>Enviado</th><th>Aceptado</th></tr>
          </thead>
          <tbody>
            {presupuestos.map(p => (
              <tr key={p.id} className="ed-row-tr" onClick={() => nav(`/admin/presupuestos/${p.id}`)}>
                <td><strong>{p.numero}</strong></td>
                <td>{formatFecha(p.fecha)}</td>
                <td>{p.validez_dias} días</td>
                <td><EstadoBadge estado={p.estado} /></td>
                <td className="ed-num">${Number(p.total).toLocaleString('es-UY')}</td>
                <td className="ed-num">{p.enviado_at ? formatFecha(p.enviado_at) : '—'}</td>
                <td className="ed-num">{p.aceptado_at ? formatFecha(p.aceptado_at) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function EstadoBadge({ estado }) {
  const map = {
    borrador:   { l: 'Borrador',  bg: '#F1F5F9', fg: '#64748B' },
    enviado:    { l: 'Enviado',   bg: '#E0F2FE', fg: '#0EA5E9' },
    aceptado:   { l: 'Aceptado',  bg: '#DCFCE7', fg: '#16A34A' },
    rechazado:  { l: 'Rechazado', bg: '#FEE2E2', fg: '#DC2626' },
    vencido:    { l: 'Vencido',   bg: '#FEF3C7', fg: '#CA8A04' },
  }
  const m = map[estado] || map.borrador
  return <span className="ed-badge" style={{ background: m.bg, color: m.fg }}>{m.l}</span>
}

// ─────────────────────────────────────────────────────────
// Tab 4: Interacciones (timeline + alta)
// ─────────────────────────────────────────────────────────
function TabInteracciones({ empresaId }) {
  const [items, setItems] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(null)

  async function cargar() {
    setCargando(true)
    const { data, error: e } = await supabase
      .from('empresas_interacciones')
      .select('*, autor:profiles!empresas_interacciones_autor_id_fkey(id, nombre)')
      .eq('empresa_id', empresaId)
      .order('fecha', { ascending: false })
      .order('created_at', { ascending: false })
    if (e) setError(e.message)
    setItems(data || [])
    setCargando(false)
  }
  useEffect(() => { cargar() }, [empresaId])

  async function guardar(form) {
    const { data: { user } } = await supabase.auth.getUser()
    const payload = {
      empresa_id: empresaId,
      tipo: form.tipo,
      fecha: form.fecha,
      autor_id: user?.id || null,
      resumen: form.resumen.trim() || null,
      notas: form.notas.trim() || null,
      proxima_accion: form.proxima_accion.trim() || null,
      proxima_fecha: form.proxima_fecha || null,
    }
    const { error: e } = await supabase.from('empresas_interacciones').insert(payload)
    if (e) {
      setError(e.message)
      return false
    }
    setError(null)
    setModal(null)
    cargar()
    return true
  }

  async function marcarCompletada(id) {
    await supabase.from('empresas_interacciones').update({ proxima_completada: true }).eq('id', id)
    cargar()
  }

  return (
    <div>
      <div className="ed-toolbar">
        <button className="ed-btn-primary" onClick={() => setModal({})}>+ Nueva interacción</button>
      </div>

      {error && <div className="ed-error">⚠ {error}</div>}

      {cargando ? (
        <div className="ed-loading-thin">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="ed-empty">
          <p>Sin interacciones registradas.</p>
          <p className="ed-empty-sub">Cada llamada, mail o reunión queda acá — para no perder el hilo.</p>
        </div>
      ) : (
        <ul className="ed-timeline">
          {items.map(i => (
            <li key={i.id} className="ed-tl-item">
              <div className="ed-tl-tipo">{tipoInteraccionLabel(i.tipo)}</div>
              <div className="ed-tl-body">
                <div className="ed-tl-fecha">
                  {formatFecha(i.fecha)} {i.autor?.nombre && `· ${i.autor.nombre}`}
                </div>
                {i.resumen && <div className="ed-tl-resumen">{i.resumen}</div>}
                {i.notas && <div className="ed-tl-notas">{i.notas}</div>}
                {i.proxima_accion && (
                  <div className={`ed-tl-prox${i.proxima_completada ? ' done' : ''}`}>
                    <span className="ed-tl-prox-label">↪ Próxima acción:</span> {i.proxima_accion}
                    {i.proxima_fecha && <span className="ed-tl-prox-fecha"> ({formatFecha(i.proxima_fecha)})</span>}
                    {!i.proxima_completada && (
                      <button className="ed-btn-link" onClick={() => marcarCompletada(i.id)}>✓ Hecha</button>
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {modal && <ModalInteraccion onSave={guardar} onClose={() => setModal(null)} />}
    </div>
  )
}

function tipoInteraccionLabel(t) {
  const m = { llamada: '📞 Llamada', whatsapp: '💬 WhatsApp', email: '✉️ Email', reunion: '🤝 Reunión', cotizacion: '📄 Cotización', nota: '📝 Nota' }
  return m[t] || t
}

function ModalInteraccion({ onSave, onClose }) {
  const [form, setForm] = useState({
    tipo: 'llamada',
    fecha: new Date().toISOString().slice(0, 10),
    resumen: '',
    notas: '',
    proxima_accion: '',
    proxima_fecha: '',
  })
  const [guardando, setGuardando] = useState(false)
  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function submit() {
    setGuardando(true)
    await onSave(form)
    setGuardando(false)
  }

  return (
    <div className="emp-modal-bg" onClick={onClose}>
      <div className="emp-modal" onClick={e => e.stopPropagation()}>
        <header className="emp-modal-head">
          <h3>Nueva interacción</h3>
          <button className="emp-modal-close" onClick={onClose}>✕</button>
        </header>
        <div className="emp-modal-body">
          <div className="ed-grid-2">
            <Field label="Tipo">
              <select value={form.tipo} onChange={e => setF('tipo', e.target.value)}>
                <option value="llamada">📞 Llamada</option>
                <option value="whatsapp">💬 WhatsApp</option>
                <option value="email">✉️ Email</option>
                <option value="reunion">🤝 Reunión</option>
                <option value="cotizacion">📄 Cotización</option>
                <option value="nota">📝 Nota</option>
              </select>
            </Field>
            <Field label="Fecha">
              <input type="date" value={form.fecha} onChange={e => setF('fecha', e.target.value)} />
            </Field>
          </div>
          <Field label="Resumen (una línea)">
            <input value={form.resumen} onChange={e => setF('resumen', e.target.value)} placeholder="Ej: Confirmó interés, pide cotización 3 edificios" />
          </Field>
          <Field label="Notas / detalle">
            <textarea rows={3} value={form.notas} onChange={e => setF('notas', e.target.value)} />
          </Field>
          <div className="ed-grid-2">
            <Field label="Próxima acción">
              <input value={form.proxima_accion} onChange={e => setF('proxima_accion', e.target.value)} placeholder="Llamar viernes, mandar cotización lunes…" />
            </Field>
            <Field label="¿Cuándo?">
              <input type="date" value={form.proxima_fecha} onChange={e => setF('proxima_fecha', e.target.value)} />
            </Field>
          </div>
        </div>
        <footer className="emp-modal-foot">
          <button className="emp-btn-secondary" onClick={onClose} disabled={guardando}>Cancelar</button>
          <button className="emp-btn-primary" onClick={submit} disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</button>
        </footer>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// Tab 5: Servicio (suscripciones B2B activas + pedidos one-shot)
// ─────────────────────────────────────────────────────────
function TabServicio({ empresaId }) {
  const [suscripciones, setSuscripciones] = useState([])
  const [pedidos, setPedidos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [accion, setAccion] = useState(null)

  async function cargar() {
    setCargando(true)
    const [{ data: subs, error: e1 }, { data: pds }] = await Promise.all([
      supabase.from('suscripciones_empresa').select('*, direccion:empresas_direcciones(alias, calle, numero, barrio:barrios(nombre))').eq('empresa_id', empresaId).order('created_at', { ascending: false }),
      supabase.from('pedidos').select('id, numero_orden, fecha_entrega_programada, estado, monto_total, kit_codigo').eq('empresa_id', empresaId).order('created_at', { ascending: false }),
    ])
    if (e1) setError(e1.message)
    setSuscripciones(subs || [])
    setPedidos(pds || [])
    setCargando(false)
  }

  useEffect(() => { cargar() }, [empresaId])

  async function pausar(susc) {
    const hasta = prompt('¿Hasta cuándo pausamos? (YYYY-MM-DD o vacío para indefinido)', '')
    if (hasta === null) return
    setAccion(susc.id)
    const motivo = prompt('Motivo (opcional)', '') || null
    const { error: e } = await supabase.from('suscripciones_empresa').update({
      estado: 'pausada',
      pausada_at: new Date().toISOString(),
      pausada_hasta: hasta || null,
      pausada_motivo: motivo,
    }).eq('id', susc.id)
    setAccion(null)
    if (e) alert('Error: ' + e.message)
    else cargar()
  }

  async function reactivar(susc) {
    if (!confirm(`Reactivar "${susc.alias}"?`)) return
    setAccion(susc.id)
    const { error: e } = await supabase.from('suscripciones_empresa').update({
      estado: 'activa',
      pausada_at: null,
      pausada_hasta: null,
      pausada_motivo: null,
    }).eq('id', susc.id)
    setAccion(null)
    if (e) alert('Error: ' + e.message)
    else cargar()
  }

  async function cancelar(susc) {
    const motivo = prompt(`Cancelar "${susc.alias}". Motivo (opcional):`, '')
    if (motivo === null) return
    if (!confirm(`Confirmás cancelar la suscripción "${susc.alias}"? Esta acción no se puede deshacer (pero queda en historial).`)) return
    setAccion(susc.id)
    const { error: e } = await supabase.from('suscripciones_empresa').update({
      estado: 'cancelada',
      cancelada_at: new Date().toISOString(),
      cancelada_motivo: motivo || null,
    }).eq('id', susc.id)
    setAccion(null)
    if (e) alert('Error: ' + e.message)
    else cargar()
  }

  if (cargando) return <div className="ed-loading-thin">Cargando…</div>

  if (suscripciones.length === 0 && pedidos.length === 0) {
    return (
      <div className="ed-empty">
        <p>Esta empresa todavía no tiene servicio activo.</p>
        <p className="ed-empty-sub">Cuando se acepte un presupuesto y se active la suscripción, aparece acá.</p>
      </div>
    )
  }

  const activas = suscripciones.filter(s => s.estado === 'activa')
  const pausadas = suscripciones.filter(s => s.estado === 'pausada')
  const canceladas = suscripciones.filter(s => s.estado === 'cancelada')

  return (
    <div>
      {error && <div className="ed-error">⚠ {error}</div>}

      {activas.length > 0 && (
        <>
          <h3 className="ed-section-h">🟢 Suscripciones activas ({activas.length})</h3>
          <div className="ed-sub-grid">
            {activas.map(s => (
              <SuscripcionCard key={s.id} s={s} accion={accion} onPausar={pausar} onCancelar={cancelar} />
            ))}
          </div>
        </>
      )}

      {pausadas.length > 0 && (
        <>
          <h3 className="ed-section-h">🟡 Pausadas ({pausadas.length})</h3>
          <div className="ed-sub-grid">
            {pausadas.map(s => (
              <SuscripcionCard key={s.id} s={s} accion={accion} onReactivar={reactivar} onCancelar={cancelar} />
            ))}
          </div>
        </>
      )}

      {canceladas.length > 0 && (
        <>
          <h3 className="ed-section-h">⚫ Canceladas ({canceladas.length})</h3>
          <div className="ed-sub-grid">
            {canceladas.map(s => (
              <SuscripcionCard key={s.id} s={s} historico />
            ))}
          </div>
        </>
      )}

      {pedidos.length > 0 && (
        <>
          <h3 className="ed-section-h">🛍️ Pedidos one-shot ({pedidos.length})</h3>
          <table className="ed-table">
            <thead><tr><th>Nº orden</th><th>Entrega</th><th>Estado</th><th>Total</th></tr></thead>
            <tbody>
              {pedidos.map(p => (
                <tr key={p.id}>
                  <td><strong>{p.numero_orden}</strong></td>
                  <td>{formatFecha(p.fecha_entrega_programada)}</td>
                  <td>{p.estado}</td>
                  <td className="ed-num">${Number(p.monto_total).toLocaleString('es-UY')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

function SuscripcionCard({ s, accion, onPausar, onReactivar, onCancelar, historico }) {
  const nav = useNavigate()
  const dir = s.direccion

  function abrir(e) {
    // Si el click viene de un botón de acción, no navegar
    if (e.target.closest('.ed-sub-card-actions')) return
    nav(`/admin/suscripciones-empresa/${s.id}`)
  }

  return (
    <div className={`ed-sub-card clickable${historico ? ' historico' : ''}`} onClick={abrir} role="button" tabIndex={0}>
      <header className="ed-sub-card-head">
        <strong>{s.alias}</strong>
        <span className="ed-sub-precio">${Number(s.precio_mensual_total).toLocaleString('es-UY')}<small>/mes</small></span>
      </header>
      {dir && (
        <div className="ed-sub-card-dir">
          📍 {dir.calle} {dir.numero || ''}{dir.barrio?.nombre ? ` · ${dir.barrio.nombre}` : ''}
        </div>
      )}
      <div className="ed-sub-card-meta">
        🗓️ {s.dia_entrega} · {ventanaLabel(s.ventana_horaria)}
        {s.items && Array.isArray(s.items) && (
          <> · {s.items.length} item{s.items.length === 1 ? '' : 's'}</>
        )}
      </div>
      {s.estado === 'pausada' && s.pausada_hasta && (
        <div className="ed-sub-card-pausada">
          Pausada hasta {formatFecha(s.pausada_hasta)}
          {s.pausada_motivo && <> · {s.pausada_motivo}</>}
        </div>
      )}
      {s.estado === 'cancelada' && (
        <div className="ed-sub-card-cancelada">
          Cancelada {formatFecha(s.cancelada_at)}
          {s.cancelada_motivo && <> · {s.cancelada_motivo}</>}
        </div>
      )}
      <div className="ed-sub-card-actions" onClick={e => e.stopPropagation()}>
        {!historico && onPausar && <button className="ed-btn-link" disabled={accion === s.id} onClick={() => onPausar(s)}>⏸ Pausar</button>}
        {!historico && onReactivar && <button className="ed-btn-link" disabled={accion === s.id} onClick={() => onReactivar(s)}>▶ Reactivar</button>}
        {!historico && onCancelar && <button className="ed-btn-link ed-btn-danger" disabled={accion === s.id} onClick={() => onCancelar(s)}>✕ Cancelar</button>}
        <span className="ed-sub-card-ver">Ver detalle →</span>
      </div>
    </div>
  )
}

function ventanaLabel(v) {
  return { manana: '🌅 Mañana 10–14', tarde: '🌇 Tarde 14–18', noche: '🌆 Tarde-noche 18–21' }[v] || v
}

// ─────────────────────────────────────────────────────────
// Tab 6: Facturas emitidas (CFEs cargados manualmente o sync Zsoftware)
// ─────────────────────────────────────────────────────────
const TIPO_CFE_LABEL = {
  e_factura:        '📄 e-Factura',
  e_ticket:         '🎫 e-Ticket',
  e_nota_credito:   '↩ Nota de Crédito',
  e_nota_debito:    '➕ Nota de Débito',
  e_resguardo:      '🛡 Resguardo',
  otro:             '📑 Otro',
}

function TabFacturas({ empresa }) {
  const [facturas, setFacturas] = useState([])
  const [suscripciones, setSuscripciones] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(null) // null | {id?, ...}
  const [descargando, setDescargando] = useState(null)

  async function cargar() {
    setCargando(true)
    const [{ data: fs, error: e1 }, { data: subs }] = await Promise.all([
      supabase.from('facturas_emitidas').select('*, suscripcion:suscripciones_empresa(id, alias), creado_por:profiles!facturas_emitidas_created_by_fkey(nombre)').eq('empresa_id', empresa.id).order('fecha_emision', { ascending: false }),
      supabase.from('suscripciones_empresa').select('id, alias').eq('empresa_id', empresa.id).order('alias'),
    ])
    if (e1) setError(e1.message)
    setFacturas(fs || [])
    setSuscripciones(subs || [])
    setCargando(false)
  }
  useEffect(() => { cargar() }, [empresa.id])

  async function descargar(factura, tipo) {
    const path = tipo === 'pdf' ? factura.pdf_path : factura.xml_path
    if (!path) return
    setDescargando(`${factura.id}-${tipo}`)
    try {
      const { data, error: e } = await supabase.storage.from('facturas-emitidas').createSignedUrl(path, 300)
      if (e) throw e
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
    } catch (err) {
      alert('No se pudo descargar: ' + err.message)
    } finally {
      setDescargando(null)
    }
  }

  async function anular(factura) {
    const motivo = prompt('Motivo de anulación (opcional):', '')
    if (motivo === null) return
    if (!confirm(`Marcar la factura ${factura.serie || ''} ${factura.numero} como ANULADA?`)) return
    const { data: { user } } = await supabase.auth.getUser()
    const { error: e } = await supabase.from('facturas_emitidas').update({
      estado: 'anulada',
      anulada_at: new Date().toISOString(),
      anulada_motivo: motivo || null,
      anulada_por: user?.id || null,
    }).eq('id', factura.id)
    if (e) alert('Error: ' + e.message)
    else cargar()
  }

  // Envía la factura por email vía edge fn enviar-factura-email desde hola@limpack.uy
  // con el PDF adjunto. Si ya se envió antes, prompt para elegir email destino (por default
  // el que está en la empresa).
  const [enviandoEmail, setEnviandoEmail] = useState(null)
  async function enviarPorEmail(factura) {
    const yaEnviado = !!factura.email_enviado_at
    const emailDefault = factura.email_enviado_a || empresa.email_facturacion || ''
    const emailFinal = prompt(
      yaEnviado
        ? `Reenviar ${factura.serie ? factura.serie + '-' : ''}${factura.numero} a:`
        : `Enviar ${factura.serie ? factura.serie + '-' : ''}${factura.numero} por email a:`,
      emailDefault,
    )
    if (emailFinal === null) return
    if (!emailFinal.trim() || !emailFinal.includes('@')) { alert('Email inválido'); return }
    setEnviandoEmail(factura.id)
    try {
      const { data, error: e } = await supabase.functions.invoke('enviar-factura-email', {
        body: { factura_id: factura.id, to: emailFinal.trim() },
      })
      if (e) throw new Error(e.message || 'Error invocando edge fn')
      if (data?.error) throw new Error(data.error + (data.detalle ? ' · ' + JSON.stringify(data.detalle).slice(0, 120) : ''))
      alert(`✓ Enviado a ${data?.to || emailFinal}${data?.con_adjunto ? ' con PDF adjunto' : ' (sin PDF adjunto — no se pudo bajar de storage)'}`)
      cargar()
    } catch (err) {
      alert('No se pudo enviar: ' + err.message)
    } finally {
      setEnviandoEmail(null)
    }
  }

  async function eliminar(factura) {
    if (!confirm(`Eliminar el registro de la factura ${factura.serie || ''} ${factura.numero}? Esto borra el archivo del storage. Solo úsalo si fue cargada por error.`)) return
    // Borrar archivos primero
    const paths = [factura.pdf_path, factura.xml_path].filter(Boolean)
    if (paths.length > 0) {
      await supabase.storage.from('facturas-emitidas').remove(paths)
    }
    const { error: e } = await supabase.from('facturas_emitidas').delete().eq('id', factura.id)
    if (e) alert('Error: ' + e.message)
    else cargar()
  }

  // Emitidas = las que ya existen en Biller y NO están anuladas
  // (estado 'pendiente' significa esperando respuesta DGI, ya está emitida en Biller)
  const totales = useMemo(() => {
    const emitidas = facturas.filter(f => f.estado === 'emitida' || f.estado === 'pendiente')
    const anuladas = facturas.filter(f => f.estado === 'anulada')
    const sumaEmitidas = emitidas.reduce((acc, f) => acc + Number(f.monto_total || 0), 0)
    return { emitidas: emitidas.length, anuladas: anuladas.length, monto: sumaEmitidas }
  }, [facturas])

  return (
    <div>
      <div className="ed-toolbar">
        <div className="ed-fact-resumen">
          <strong>{totales.emitidas}</strong> emitidas · <strong>${totales.monto.toLocaleString('es-UY')}</strong> facturado
          {totales.anuladas > 0 && <> · {totales.anuladas} anuladas</>}
        </div>
        <button className="ed-btn-primary" onClick={() => setModal({ empresa_id: empresa.id })}>+ Subir factura</button>
      </div>

      {error && <div className="ed-error">⚠ {error}</div>}

      {cargando ? (
        <div className="ed-loading-thin">Cargando…</div>
      ) : facturas.length === 0 ? (
        <div className="ed-empty">
          <p>Sin facturas registradas todavía.</p>
          <p className="ed-empty-sub">
            Cargá manualmente las que Roberto emite afuera. Cuando se integre Zsoftware se va a poblar automáticamente al cobrar cada suscripción.
          </p>
        </div>
      ) : (
        <table className="ed-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Nº</th>
              <th>Fecha</th>
              <th>Vinculada a</th>
              <th>Monto</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {facturas.map(f => (
              <tr key={f.id} className={f.estado === 'anulada' ? 'ed-fact-anulada' : ''}>
                <td>{TIPO_CFE_LABEL[f.tipo_cfe] || f.tipo_cfe}</td>
                <td><strong>{f.serie ? `${f.serie}-` : ''}{f.numero}</strong></td>
                <td>{formatFecha(f.fecha_emision)}</td>
                <td>{f.suscripcion?.alias || (f.pedido_id ? `Pedido one-shot` : '—')}</td>
                <td className="ed-num"><strong>${Number(f.monto_total).toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></td>
                <td>
                  {f.estado === 'emitida' && (
                    <span className="ed-badge" style={{ background: '#DCFCE7', color: '#16A34A' }}>Emitida</span>
                  )}
                  {f.estado === 'pendiente' && (
                    <span
                      className="ed-badge"
                      style={{ background: '#DBEAFE', color: '#1D4ED8' }}
                      title={f.estado_dgi ? `Estado DGI: ${f.estado_dgi}` : 'Emitida en Biller, esperando respuesta DGI'}
                    >
                      Emitida
                    </span>
                  )}
                  {f.estado === 'anulada' && (
                    <span className="ed-badge" style={{ background: '#FEE2E2', color: '#DC2626' }}>Anulada</span>
                  )}
                  {!['emitida', 'pendiente', 'anulada'].includes(f.estado) && (
                    <span className="ed-badge" style={{ background: '#F1F5F9', color: '#475569' }}>{f.estado || '—'}</span>
                  )}
                  {f.sync_origen === 'zsoftware' && <span className="ed-pill-muted">Zsoftware</span>}
                </td>
                <td>
                  <div className="ed-fact-actions">
                    {f.pdf_path && (
                      <button className="ed-btn-link" disabled={descargando === `${f.id}-pdf`} onClick={() => descargar(f, 'pdf')}>
                        {descargando === `${f.id}-pdf` ? '…' : '📄 PDF'}
                      </button>
                    )}
                    {f.xml_path && (
                      <button className="ed-btn-link" disabled={descargando === `${f.id}-xml`} onClick={() => descargar(f, 'xml')}>
                        {descargando === `${f.id}-xml` ? '…' : '📋 XML'}
                      </button>
                    )}
                    {f.estado !== 'anulada' && (
                      <button
                        className="ed-btn-link"
                        disabled={enviandoEmail === f.id}
                        onClick={() => enviarPorEmail(f)}
                        title={f.email_enviado_at
                          ? `Enviado a ${f.email_enviado_a || '?'} el ${new Date(f.email_enviado_at).toLocaleString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                          : (f.email_error ? '⚠ Error previo: ' + f.email_error : 'Enviar por email al cliente')}
                        style={f.email_error ? { color: '#DC2626' } : undefined}
                      >
                        {enviandoEmail === f.id ? '…' : f.email_enviado_at ? '📧 Reenviar' : (f.email_error ? '⚠ Reintentar' : '📧 Enviar')}
                      </button>
                    )}
                    <button className="ed-btn-link" onClick={() => setModal(f)}>Editar</button>
                    {(f.estado === 'emitida' || f.estado === 'pendiente') && (
                      <button className="ed-btn-link" onClick={() => anular(f)} title="Marcar como anulada">⛔ Anular</button>
                    )}
                    <button className="ed-btn-link ed-btn-danger" onClick={() => eliminar(f)} title="Borrar registro (solo si fue error)">🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <ModalFactura
          factura={modal}
          empresa={empresa}
          suscripciones={suscripciones}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); cargar() }}
        />
      )}
    </div>
  )
}

function ModalFactura({ factura, empresa, suscripciones, onClose, onSaved }) {
  const esEdit = !!factura.id
  const [form, setForm] = useState({
    tipo_cfe: factura.tipo_cfe || 'e_factura',
    serie: factura.serie || '',
    numero: factura.numero || '',
    fecha_emision: factura.fecha_emision || new Date().toISOString().slice(0, 10),
    receptor_razon_social: factura.receptor_razon_social || empresa.razon_social || '',
    receptor_rut: factura.receptor_rut || empresa.rut || '',
    subtotal: factura.subtotal || '',
    iva_total: factura.iva_total || '',
    monto_total: factura.monto_total || '',
    moneda: factura.moneda || 'UYU',
    suscripcion_empresa_id: factura.suscripcion_empresa_id || '',
    notas: factura.notas || '',
  })
  const [pdfFile, setPdfFile] = useState(null)
  const [xmlFile, setXmlFile] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function subirArchivo(file, ext) {
    const nombre = `${empresa.id}/${form.tipo_cfe}-${form.serie || 'X'}-${form.numero}-${Date.now()}.${ext}`
    const { error: e } = await supabase.storage.from('facturas-emitidas').upload(nombre, file, { contentType: file.type, upsert: false })
    if (e) throw e
    return nombre
  }

  async function guardar() {
    setError(null)
    if (!form.numero.trim()) { setError('Número de CFE obligatorio'); return }
    if (!form.monto_total || Number(form.monto_total) <= 0) { setError('Monto total debe ser mayor a 0'); return }
    setGuardando(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const payload = {
        empresa_id: empresa.id,
        tipo_cfe: form.tipo_cfe,
        serie: form.serie.trim() || null,
        numero: form.numero.trim(),
        fecha_emision: form.fecha_emision,
        receptor_razon_social: form.receptor_razon_social.trim() || null,
        receptor_rut: form.receptor_rut.trim() || null,
        subtotal: Number(form.subtotal) || 0,
        iva_total: Number(form.iva_total) || 0,
        monto_total: Number(form.monto_total),
        moneda: form.moneda || 'UYU',
        suscripcion_empresa_id: form.suscripcion_empresa_id || null,
        notas: form.notas.trim() || null,
        sync_origen: 'manual',
      }

      // Subir archivos primero (si hay)
      if (pdfFile) payload.pdf_path = await subirArchivo(pdfFile, 'pdf')
      if (xmlFile) payload.xml_path = await subirArchivo(xmlFile, 'xml')

      if (esEdit) {
        const { error: e } = await supabase.from('facturas_emitidas').update(payload).eq('id', factura.id)
        if (e) throw e
      } else {
        payload.created_by = user?.id || null
        const { error: e } = await supabase.from('facturas_emitidas').insert(payload)
        if (e) throw e
      }
      onSaved()
    } catch (err) {
      setError(err.message || 'Error al guardar')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="emp-modal-bg" onClick={onClose}>
      <div className="emp-modal" onClick={e => e.stopPropagation()}>
        <header className="emp-modal-head">
          <h3>{esEdit ? 'Editar factura' : 'Subir factura emitida'}</h3>
          <button className="emp-modal-close" onClick={onClose}>✕</button>
        </header>
        <div className="emp-modal-body">
          <div className="ed-grid-2">
            <Field label="Tipo">
              <select value={form.tipo_cfe} onChange={e => setF('tipo_cfe', e.target.value)}>
                {Object.entries(TIPO_CFE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Fecha emisión">
              <input type="date" value={form.fecha_emision} onChange={e => setF('fecha_emision', e.target.value)} />
            </Field>
          </div>
          <div className="ed-grid-2">
            <Field label="Serie">
              <input value={form.serie} onChange={e => setF('serie', e.target.value)} placeholder="A" />
            </Field>
            <Field label="Número *">
              <input value={form.numero} onChange={e => setF('numero', e.target.value)} placeholder="0001234" />
            </Field>
          </div>

          <Field label="Vinculada a suscripción (opcional)">
            <select value={form.suscripcion_empresa_id} onChange={e => setF('suscripcion_empresa_id', e.target.value)}>
              <option value="">— Sin vincular (factura suelta) —</option>
              {suscripciones.map(s => <option key={s.id} value={s.id}>{s.alias}</option>)}
            </select>
          </Field>

          <div className="ed-grid-2">
            <Field label="Razón social receptor">
              <input value={form.receptor_razon_social} onChange={e => setF('receptor_razon_social', e.target.value)} />
            </Field>
            <Field label="RUT receptor">
              <input value={form.receptor_rut} onChange={e => setF('receptor_rut', e.target.value)} />
            </Field>
          </div>

          <div className="ed-grid-3">
            <Field label="Subtotal s/IVA">
              <input type="number" min="0" step="0.01" value={form.subtotal} onChange={e => setF('subtotal', e.target.value)} />
            </Field>
            <Field label="IVA">
              <input type="number" min="0" step="0.01" value={form.iva_total} onChange={e => setF('iva_total', e.target.value)} />
            </Field>
            <Field label="Total *">
              <input type="number" min="0" step="0.01" value={form.monto_total} onChange={e => setF('monto_total', e.target.value)} />
            </Field>
          </div>

          <Field label="Archivo PDF">
            <input type="file" accept="application/pdf" onChange={e => setPdfFile(e.target.files?.[0] || null)} />
            {esEdit && factura.pdf_path && !pdfFile && (
              <div className="ed-fact-actual">📄 Ya hay PDF cargado. Subí otro para reemplazarlo.</div>
            )}
          </Field>

          <Field label="Archivo XML (opcional)">
            <input type="file" accept=".xml,application/xml,text/xml" onChange={e => setXmlFile(e.target.files?.[0] || null)} />
            {esEdit && factura.xml_path && !xmlFile && (
              <div className="ed-fact-actual">📋 Ya hay XML cargado. Subí otro para reemplazarlo.</div>
            )}
          </Field>

          <Field label="Notas internas">
            <textarea rows={2} value={form.notas} onChange={e => setF('notas', e.target.value)} />
          </Field>

          {error && <div className="ed-error">⚠ {error}</div>}
        </div>
        <footer className="emp-modal-foot">
          <button className="emp-btn-secondary" onClick={onClose} disabled={guardando}>Cancelar</button>
          <button className="emp-btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : (esEdit ? 'Guardar cambios' : 'Subir factura')}
          </button>
        </footer>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// Tab: Regalos (historial de regalos a este cliente + botón "Registrar")
// ─────────────────────────────────────────────────────────
function TabRegalos({ empresaId, empresaNombre }) {
  const [regalos, setRegalos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [modalAbierto, setModalAbierto] = useState(false)
  const [msg, setMsg] = useState(null)

  async function cargar() {
    setCargando(true)
    const { data, error } = await supabase
      .from('v_regalos_clientes')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false })
    if (error) console.error(error)
    setRegalos(data || [])
    setCargando(false)
  }

  useEffect(() => { cargar() }, [empresaId])

  const totalUnidades = regalos.reduce((s, r) => s + Number(r.cantidad), 0)
  const fmtFecha = d => new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' })

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, color: '#1A3461', fontSize: 17 }}>🎁 Regalos a {empresaNombre}</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748B' }}>
            {regalos.length} regalo{regalos.length !== 1 ? 's' : ''} · {totalUnidades} unidad{totalUnidades !== 1 ? 'es' : ''} en total
          </p>
        </div>
        <button className="emp-btn-primary" onClick={() => setModalAbierto(true)}>+ Registrar regalo</button>
      </div>

      {msg && (
        <div style={{
          padding: 10, marginBottom: 12,
          background: msg.tipo === 'ok' ? '#D1FAE5' : '#FEE2E2',
          border: `1px solid ${msg.tipo === 'ok' ? '#86EFAC' : '#FCA5A5'}`,
          borderRadius: 6, color: msg.tipo === 'ok' ? '#065F46' : '#991B1B', fontSize: 12,
        }}>
          {msg.txt}
        </div>
      )}

      {cargando ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#64748B', fontSize: 13 }}>Cargando…</div>
      ) : regalos.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', background: '#F8FAFC', borderRadius: 10, color: '#64748B' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🎁</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Aún no le regalaste nada a este cliente</div>
          <div style={{ fontSize: 12 }}>Registrá el primer regalo con el botón de arriba.</div>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0', fontSize: 10, textTransform: 'uppercase', color: '#64748B' }}>
                <th style={{ padding: 10, textAlign: 'left' }}>Fecha</th>
                <th style={{ padding: 10, textAlign: 'left' }}>Producto</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Cantidad</th>
                <th style={{ padding: 10, textAlign: 'left' }}>Pedido</th>
                <th style={{ padding: 10, textAlign: 'left' }}>Motivo</th>
                <th style={{ padding: 10, textAlign: 'left' }}>Registrado por</th>
              </tr>
            </thead>
            <tbody>
              {regalos.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ padding: 10, color: '#64748B' }}>{fmtFecha(r.created_at)}</td>
                  <td style={{ padding: 10 }}>
                    <div style={{ fontWeight: 700, color: '#1A3461' }}>{r.producto_marca ? `${r.producto_marca} · ` : ''}{r.producto_nombre}</div>
                    <div style={{ fontSize: 11, color: '#64748B' }}>{r.producto_presentacion}</div>
                  </td>
                  <td style={{ padding: 10, textAlign: 'right', fontWeight: 700, color: '#DC2626' }}>
                    −{Number(r.cantidad)}
                  </td>
                  <td style={{ padding: 10, fontSize: 12 }}>{r.pedido_numero ? <code style={{ color: '#0369A1' }}>{r.pedido_numero}</code> : '—'}</td>
                  <td style={{ padding: 10, fontSize: 12, color: '#334155' }}>{r.motivo || '—'}</td>
                  <td style={{ padding: 10, fontSize: 12, color: '#64748B' }}>{r.created_by_nombre || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalAbierto && (
        <ModalRegistrarRegalo
          empresaId={empresaId}
          empresaNombre={empresaNombre}
          onClose={() => setModalAbierto(false)}
          onDone={(nombreProducto, cantidad) => {
            setModalAbierto(false)
            setMsg({ tipo: 'ok', txt: `✅ Regalo registrado: ${cantidad}× ${nombreProducto}. Stock actualizado.` })
            cargar()
            setTimeout(() => setMsg(null), 5000)
          }}
        />
      )}
    </div>
  )
}

// Modal para registrar un regalo nuevo
function ModalRegistrarRegalo({ empresaId, empresaNombre, onClose, onDone }) {
  const [productos, setProductos] = useState([])
  const [pedidos, setPedidos] = useState([])
  const [busqProducto, setBusqProducto] = useState('')
  const [productoElegido, setProductoElegido] = useState(null)
  const [cantidad, setCantidad] = useState(1)
  const [pedidoId, setPedidoId] = useState('')
  const [motivo, setMotivo] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    (async () => {
      const [{ data: prods }, { data: peds }] = await Promise.all([
        supabase.from('productos').select('id, nombre, marca, presentacion, stock_actual').eq('activo', true).gt('stock_actual', 0).order('nombre'),
        supabase.from('pedidos').select('id, numero_orden, created_at, total').eq('empresa_id', empresaId).order('created_at', { ascending: false }).limit(5),
      ])
      setProductos(prods || [])
      setPedidos(peds || [])
    })()
  }, [empresaId])

  const productosFiltrados = productos.filter(p => {
    if (!busqProducto.trim()) return true
    const q = busqProducto.toLowerCase()
    return (p.nombre || '').toLowerCase().includes(q)
      || (p.marca || '').toLowerCase().includes(q)
      || (p.presentacion || '').toLowerCase().includes(q)
  }).slice(0, 8)

  async function guardar(e) {
    e.preventDefault()
    setErr(null)
    if (!productoElegido) { setErr('Elegí un producto'); return }
    if (!cantidad || Number(cantidad) <= 0) { setErr('Cantidad debe ser > 0'); return }
    if (Number(cantidad) > Number(productoElegido.stock_actual)) {
      setErr(`Stock insuficiente: hay ${productoElegido.stock_actual}, querés regalar ${cantidad}`)
      return
    }
    if (!motivo.trim()) { setErr('Contame el motivo del regalo'); return }

    setGuardando(true)
    const { error } = await supabase.rpc('registrar_regalo', {
      p_producto_id: productoElegido.id,
      p_cantidad: Number(cantidad),
      p_empresa_id: empresaId,
      p_pedido_id: pedidoId || null,
      p_motivo: motivo.trim(),
    })
    setGuardando(false)
    if (error) { setErr('Error: ' + error.message); return }
    onDone(
      [productoElegido.marca, productoElegido.nombre, productoElegido.presentacion].filter(Boolean).join(' · '),
      cantidad,
    )
  }

  return (
    <div className="emp-modal-bg" onClick={onClose}>
      <div className="emp-modal" onClick={e => e.stopPropagation()}>
        <div className="emp-modal-head">
          <h3>🎁 Registrar regalo a {empresaNombre}</h3>
          <button className="emp-modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={guardar}>
          <div className="emp-modal-body">
            <Field label="Producto a regalar" required>
              {productoElegido ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#F0FDFA', border: '1px solid #14B8A6', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#1A3461' }}>{productoElegido.marca ? `${productoElegido.marca} · ` : ''}{productoElegido.nombre}</div>
                    <div style={{ fontSize: 12, color: '#64748B' }}>{productoElegido.presentacion} · Stock: {productoElegido.stock_actual}</div>
                  </div>
                  <button type="button" onClick={() => { setProductoElegido(null); setBusqProducto('') }} style={{ background: 'transparent', border: 'none', color: '#0369A1', fontSize: 12, cursor: 'pointer' }}>Cambiar</button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={busqProducto}
                    onChange={e => setBusqProducto(e.target.value)}
                    placeholder="Buscá por nombre, marca o presentación…"
                    autoFocus
                  />
                  {busqProducto.trim() && productosFiltrados.length > 0 && (
                    <div style={{ marginTop: 4, border: '1px solid #E2E8F0', borderRadius: 8, maxHeight: 200, overflowY: 'auto' }}>
                      {productosFiltrados.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => { setProductoElegido(p); setBusqProducto('') }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'white', border: 'none', borderBottom: '1px solid #F1F5F9', cursor: 'pointer', fontSize: 13 }}
                        >
                          <div style={{ fontWeight: 600, color: '#1A3461' }}>{p.marca ? `${p.marca} · ` : ''}{p.nombre}</div>
                          <div style={{ fontSize: 11, color: '#64748B' }}>{p.presentacion} · Stock: {p.stock_actual}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {busqProducto.trim() && productosFiltrados.length === 0 && (
                    <div style={{ fontSize: 12, color: '#94A3B8', padding: 8 }}>Sin productos con stock que coincidan.</div>
                  )}
                </>
              )}
            </Field>

            <Field label="Cantidad" required>
              <input
                type="number"
                min="1"
                step="1"
                value={cantidad}
                onChange={e => setCantidad(e.target.value)}
                required
              />
            </Field>

            <Field label="Asociar a pedido (opcional)">
              <select value={pedidoId} onChange={e => setPedidoId(e.target.value)}>
                <option value="">— Regalo suelto, sin pedido —</option>
                {pedidos.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.numero_orden || 'sin nº'} · {new Date(p.created_at).toLocaleDateString('es-UY')} · ${Number(p.total || 0).toLocaleString('es-UY')}
                  </option>
                ))}
              </select>
              {pedidos.length === 0 && (
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>Esta empresa no tiene pedidos previos.</div>
              )}
            </Field>

            <Field label="Motivo" required>
              <input
                type="text"
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                placeholder="Ej: atención al nuevo cliente / disculpa por demora / navidad"
                required
              />
            </Field>

            {err && <div style={{ padding: 8, background: '#FEE2E2', color: '#991B1B', borderRadius: 6, fontSize: 12, marginTop: 8 }}>{err}</div>}
          </div>
          <div className="emp-modal-foot">
            <button type="button" className="emp-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="emp-btn-primary" disabled={guardando || !productoElegido}>
              {guardando ? 'Registrando…' : 'Registrar regalo y bajar stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// Utilidades
// ─────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────
// Tab Edificios (solo administradoras): lista de edificios con
// identidad de facturación propia + contacto de portería.
// ─────────────────────────────────────────────────────────
function TabEdificios({ empresa }) {
  const empresaId = empresa.id
  const [edificios, setEdificios] = useState([])
  const [barrios, setBarrios] = useState([])
  const [cargando, setCargando] = useState(true)
  const [modal, setModal] = useState(null)
  const [error, setError] = useState(null)

  async function cargar() {
    setCargando(true)
    const [{ data: eds, error: e1 }, { data: bs }] = await Promise.all([
      supabase.from('edificios').select('*, barrio:barrios(id, nombre)').eq('empresa_id', empresaId).order('nombre'),
      supabase.from('barrios').select('id, nombre').eq('activo', true).order('nombre'),
    ])
    if (e1) setError(e1.message)
    setEdificios(eds || [])
    setBarrios(bs || [])
    setCargando(false)
  }
  useEffect(() => { cargar() }, [empresaId])

  async function guardar(form) {
    const payload = {
      empresa_id: empresaId,
      nombre: form.nombre.trim(),
      razon_social: form.razon_social.trim() || null,
      rut: form.rut.replace(/\D/g, '') || null,
      facturacion_email: form.facturacion_email.trim() || null,
      direccion: form.direccion.trim() || null,
      apto: form.apto.trim() || null,
      barrio_id: form.barrio_id || null,
      contacto_admin_nombre: form.contacto_admin_nombre.trim() || null,
      contacto_admin_telefono: form.contacto_admin_telefono.trim() || null,
      portera_nombre: form.portera_nombre.trim() || null,
      portera_telefono: form.portera_telefono.trim() || null,
      acceso_notas: form.acceso_notas.trim() || null,
      notas: form.notas.trim() || null,
      activo: form.activo !== false,
    }
    const { error: e } = form.id
      ? await supabase.from('edificios').update(payload).eq('id', form.id)
      : await supabase.from('edificios').insert(payload)
    if (e) { setError(e.message); return false }
    setError(null); setModal(null); await cargar(); return true
  }

  async function eliminar(id) {
    if (!confirm('¿Eliminar este edificio? (los pedidos ya facturados no se tocan)')) return
    const { error: e } = await supabase.from('edificios').delete().eq('id', id)
    if (e) setError(e.message); else cargar()
  }

  const modo = empresa.facturacion_modo === 'consolidada'
    ? 'consolidada a la razón social de la administradora'
    : 'una e-Factura por edificio'

  return (
    <div>
      <div className="ed-toolbar">
        <button className="ed-btn-primary" onClick={() => setModal({})}>+ Nuevo edificio</button>
      </div>
      <div style={{ fontSize: 13, color: '#64748B', marginBottom: 12 }}>
        Modo de facturación de esta administradora: <strong>{modo}</strong>. Cada edificio puede tener su propia razón social y RUT para la e-Factura, más los datos de la portería que te atiende.
      </div>
      {error && <div className="ed-error">⚠ {error}</div>}
      {cargando ? (
        <div className="ed-loading-thin">Cargando…</div>
      ) : edificios.length === 0 ? (
        <div className="ed-empty">
          <p>Todavía no cargaste edificios para esta administradora.</p>
          <p className="ed-empty-sub">Cargá cada edificio con su razón social/RUT y el contacto de portería — después los elegís al crear el pedido B2B.</p>
        </div>
      ) : (
        <div className="ed-dir-grid">
          {edificios.map(ed => {
            const esFactura = ed.rut && ed.rut.replace(/\D/g, '').length === 12
            return (
              <div key={ed.id} className="ed-dir-card">
                <div className="ed-dir-head">
                  <strong>{ed.nombre}</strong>
                  {!ed.activo && <span className="ed-pill-muted">inactivo</span>}
                  <span className="ed-pill-muted">{esFactura ? '🏢 e-Factura' : '👤 e-Ticket'}</span>
                </div>
                {ed.razon_social && <div className="ed-dir-addr">{ed.razon_social}{ed.rut ? ` · RUT ${ed.rut}` : ''}</div>}
                {(ed.direccion || ed.barrio) && <div className="ed-dir-meta">📍 {ed.direccion || ''}{ed.apto ? ` · ${ed.apto}` : ''}{ed.barrio?.nombre ? ` · ${ed.barrio.nombre}` : ''}</div>}
                {ed.facturacion_email && <div className="ed-dir-contact">🧾 {ed.facturacion_email}</div>}
                {ed.contacto_admin_nombre && <div className="ed-dir-contact">👤 Admin: {ed.contacto_admin_nombre}{ed.contacto_admin_telefono ? ` · ${ed.contacto_admin_telefono}` : ''}</div>}
                {ed.portera_nombre && <div className="ed-dir-contact">🔑 Portería: {ed.portera_nombre}{ed.portera_telefono ? ` · ${ed.portera_telefono}` : ''}</div>}
                {ed.acceso_notas && <div className="ed-dir-obs">🚪 {ed.acceso_notas}</div>}
                {ed.notas && <div className="ed-dir-obs">{ed.notas}</div>}
                <div className="ed-dir-actions">
                  <button className="ed-btn-link" onClick={() => setModal(ed)}>Editar</button>
                  <button className="ed-btn-link ed-btn-danger" onClick={() => eliminar(ed.id)}>Eliminar</button>
                </div>
              </div>
            )
          })}
        </div>
      )}
      {modal && <ModalEdificio edificio={modal} barrios={barrios} onSave={guardar} onClose={() => setModal(null)} />}
    </div>
  )
}

function ModalEdificio({ edificio, barrios, onSave, onClose }) {
  const [form, setForm] = useState({
    id: edificio.id,
    nombre: edificio.nombre || '',
    razon_social: edificio.razon_social || '',
    rut: edificio.rut || '',
    facturacion_email: edificio.facturacion_email || '',
    direccion: edificio.direccion || '',
    apto: edificio.apto || '',
    barrio_id: edificio.barrio_id || '',
    contacto_admin_nombre: edificio.contacto_admin_nombre || '',
    contacto_admin_telefono: edificio.contacto_admin_telefono || '',
    portera_nombre: edificio.portera_nombre || '',
    portera_telefono: edificio.portera_telefono || '',
    acceso_notas: edificio.acceso_notas || '',
    notas: edificio.notas || '',
    activo: edificio.activo !== false,
  })
  const [guardando, setGuardando] = useState(false)
  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }
  async function submit() {
    if (!form.nombre.trim()) return
    setGuardando(true)
    await onSave(form)
    setGuardando(false)
  }
  return (
    <div className="emp-modal-bg" onClick={onClose}>
      <div className="emp-modal" onClick={e => e.stopPropagation()}>
        <header className="emp-modal-head">
          <h3>{edificio.id ? 'Editar edificio' : 'Nuevo edificio'}</h3>
          <button className="emp-modal-close" onClick={onClose}>✕</button>
        </header>
        <div className="emp-modal-body">
          <Field label="Nombre del edificio *">
            <input value={form.nombre} onChange={e => setF('nombre', e.target.value)} placeholder="Ej: Edificio Río Brando" autoFocus />
          </Field>
          <div className="ed-grid-2">
            <Field label="Razón social (para la e-Factura)">
              <input value={form.razon_social} onChange={e => setF('razon_social', e.target.value)} placeholder="Consorcio / propietario" />
            </Field>
            <Field label="RUT (12 dígitos → e-Factura)">
              <input value={form.rut} onChange={e => setF('rut', e.target.value)} placeholder="Sin RUT = e-Ticket" />
            </Field>
          </div>
          <Field label="Email de facturación">
            <input value={form.facturacion_email} onChange={e => setF('facturacion_email', e.target.value)} />
          </Field>
          <div className="ed-grid-3">
            <Field label="Dirección">
              <input value={form.direccion} onChange={e => setF('direccion', e.target.value)} />
            </Field>
            <Field label="Apto / detalle">
              <input value={form.apto} onChange={e => setF('apto', e.target.value)} />
            </Field>
            <Field label="Barrio">
              <select value={form.barrio_id} onChange={e => setF('barrio_id', e.target.value)}>
                <option value="">—</option>
                {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
            </Field>
          </div>
          <div className="ed-grid-2">
            <Field label="Contacto administración — nombre">
              <input value={form.contacto_admin_nombre} onChange={e => setF('contacto_admin_nombre', e.target.value)} />
            </Field>
            <Field label="Contacto administración — teléfono">
              <input value={form.contacto_admin_telefono} onChange={e => setF('contacto_admin_telefono', e.target.value)} />
            </Field>
          </div>
          <div className="ed-grid-2">
            <Field label="Portera / portero — nombre">
              <input value={form.portera_nombre} onChange={e => setF('portera_nombre', e.target.value)} placeholder="Quién te atiende" />
            </Field>
            <Field label="Portera / portero — teléfono">
              <input value={form.portera_telefono} onChange={e => setF('portera_telefono', e.target.value)} />
            </Field>
          </div>
          <Field label="Notas de acceso (horario portería, cómo entrar, timbre…)">
            <textarea rows={2} value={form.acceso_notas} onChange={e => setF('acceso_notas', e.target.value)} />
          </Field>
          <Field label="Notas">
            <textarea rows={2} value={form.notas} onChange={e => setF('notas', e.target.value)} />
          </Field>
          <label className="ed-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.activo} onChange={e => setF('activo', e.target.checked)} style={{ width: 'auto' }} />
            <span>Activo</span>
          </label>
        </div>
        <footer className="emp-modal-foot">
          <button className="ed-btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="ed-btn-primary" onClick={submit} disabled={guardando || !form.nombre.trim()}>{guardando ? 'Guardando…' : 'Guardar'}</button>
        </footer>
      </div>
    </div>
  )
}



function Field({ label, children }) {
  return (
    <label className="ed-field">
      <span>{label}</span>
      {children}
    </label>
  )
}

function formatFecha(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function Estilos() {
  return (
    <style>{`
      .ed-page { max-width: 1200px; margin: 0 auto; padding: 24px; font-family: Inter, system-ui, sans-serif; color: #1A3461; }

      .ed-back { background: transparent; border: none; color: #64748B; font-size: 13px; cursor: pointer; padding: 4px 0; margin-bottom: 8px; font-family: inherit; }
      .ed-back:hover { color: #1A3461; }

      .ed-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 18px; }
      .ed-head h1 { margin: 0 0 4px; font-size: 22px; font-weight: 900; letter-spacing: -0.02em; }
      .ed-meta { font-size: 13px; color: #64748B; display: flex; gap: 4px; flex-wrap: wrap; }
      .ed-badge { display: inline-block; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; }
      .ed-head-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
      .ed-head-menu { display: flex; gap: 4px; }
      .ed-btn-icon {
        background: #fff; border: 1px solid #E2E8F0; border-radius: 8px;
        padding: 6px 9px; cursor: pointer; font-size: 14px;
        transition: all 0.15s; font-family: inherit;
      }
      .ed-btn-icon:hover { border-color: #1A3461; background: #F8FAFC; }
      .ed-btn-icon.ed-btn-danger:hover { border-color: #DC2626; background: #FEE2E2; }

      .ed-archivada-banner {
        background: #F1F5F9; border-left: 3px solid #64748B;
        padding: 10px 14px; border-radius: 0 8px 8px 0;
        margin-bottom: 14px; font-size: 13px; color: #475569;
        display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
      }
      .ed-archivada-banner strong { color: #1A3461; }

      .ed-tabs { display: flex; gap: 4px; border-bottom: 2px solid #E2E8F0; margin-bottom: 20px; overflow-x: auto; }
      .ed-tab {
        background: transparent; border: none; padding: 10px 16px;
        font-size: 13.5px; font-weight: 600; color: #64748B;
        cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px;
        font-family: inherit; white-space: nowrap;
      }
      .ed-tab:hover { color: #1A3461; }
      .ed-tab.active { color: #1A3461; border-bottom-color: #00A8B5; }
      .ed-tab-icon { margin-right: 4px; }

      .ed-content { min-height: 480px; position: relative; }

      .ed-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 20px; }
      .ed-toolbar { display: flex; justify-content: flex-end; margin-bottom: 14px; }

      .ed-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .ed-grid-3 { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 12px; }
      @media (max-width: 640px) { .ed-grid-2, .ed-grid-3 { grid-template-columns: 1fr; } }

      .ed-field { display: block; margin-bottom: 14px; }
      .ed-field > span { display: block; font-size: 12px; font-weight: 700; color: #475569; margin-bottom: 5px; }
      .ed-field input, .ed-field select, .ed-field textarea {
        width: 100%; padding: 9px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0;
        font-size: 13.5px; font-family: inherit; color: #1A3461; background: #fff; box-sizing: border-box;
      }
      .ed-field textarea { resize: vertical; min-height: 60px; }
      .ed-field input:focus, .ed-field select:focus, .ed-field textarea:focus { outline: none; border-color: #00A8B5; }

      .ed-check { display: flex; align-items: center; gap: 8px; font-size: 13.5px; color: #1A3461; cursor: pointer; }
      .ed-check input { width: auto; }

      .ed-btn-primary, .ed-btn-secondary, .ed-btn-link {
        padding: 9px 14px; border-radius: 8px; font-size: 13px; font-weight: 700;
        font-family: inherit; cursor: pointer; transition: all 0.15s; border: none;
      }
      .ed-btn-primary { background: #1A3461; color: #fff; }
      .ed-btn-primary:hover { background: #1A3461; }
      .ed-btn-primary:disabled { opacity: 0.5; cursor: wait; }
      .ed-btn-secondary { background: #fff; color: #475569; border: 1.5px solid #E2E8F0; }
      .ed-btn-secondary:hover { border-color: #1A3461; color: #1A3461; }
      .ed-btn-link { background: transparent; padding: 4px 8px; color: #00A8B5; font-size: 12px; }
      .ed-btn-link:hover { text-decoration: underline; }
      .ed-btn-link.ed-btn-danger { color: #DC2626; }

      .ed-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }

      .ed-callout {
        background: #F0FDFA; border-left: 3px solid #00A8B5; padding: 12px 16px;
        border-radius: 0 8px 8px 0; margin: 14px 0; font-size: 13.5px; color: #134E4A;
      }
      .ed-callout-msg { margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(0,168,181,0.2); font-style: italic; }

      .ed-error { background: #FEE2E2; border-left: 3px solid #DC2626; padding: 10px 14px; border-radius: 0 8px 8px 0; color: #991B1B; font-size: 13px; margin: 10px 0; }
      .ed-ok    { background: #DCFCE7; border-left: 3px solid #16A34A; padding: 10px 14px; border-radius: 0 8px 8px 0; color: #14532D; font-size: 13px; margin: 10px 0; }
      .ed-loading { text-align: center; padding: 30px; color: #64748B; }
      /* Loading sutil para tabs — barra arriba que no provoca layout shift */
      .ed-loading-thin {
        font-size: 12px; color: #64748B;
        padding: 8px 14px; background: #F8FAFC;
        border: 1px dashed #E2E8F0; border-radius: 8px;
        margin-bottom: 12px; display: inline-flex; align-items: center; gap: 6px;
      }
      .ed-loading-thin::before {
        content: ''; width: 8px; height: 8px; border-radius: 50%;
        background: #00A8B5; animation: ed-pulse 1s ease-in-out infinite;
      }
      @keyframes ed-pulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.3 } }
      .ed-empty { background: #fff; border: 1px dashed #E2E8F0; border-radius: 12px; padding: 40px; text-align: center; color: #64748B; }
      .ed-empty p { margin: 4px 0; }
      .ed-empty-sub { font-size: 12.5px; }

      .ed-pill-muted { background: #F1F5F9; color: #64748B; font-size: 11px; padding: 2px 8px; border-radius: 999px; font-weight: 700; margin-left: 8px; }

      /* Tab Direcciones */
      .ed-dir-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 12px; }
      .ed-dir-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 14px 16px; }
      .ed-dir-head { display: flex; align-items: center; margin-bottom: 4px; }
      .ed-dir-addr { font-size: 13.5px; color: #1A3461; }
      .ed-dir-meta { font-size: 12px; color: #64748B; margin: 4px 0; }
      .ed-dir-contact { font-size: 12.5px; color: #475569; margin-top: 8px; }
      .ed-dir-obs { font-size: 12px; color: #64748B; margin-top: 6px; font-style: italic; padding-top: 6px; border-top: 1px dashed #E2E8F0; }
      .ed-dir-actions { display: flex; gap: 4px; margin-top: 10px; }

      /* Tabla compartida */
      .ed-table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(15,23,42,0.05); margin-bottom: 16px; }
      .ed-table th { background: #F8FAFC; text-align: left; padding: 10px 14px; font-size: 11.5px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #E2E8F0; }
      .ed-table td { padding: 11px 14px; border-bottom: 1px solid #F1F5F9; font-size: 13.5px; }
      .ed-table tr:last-child td { border-bottom: none; }
      .ed-row-tr { cursor: pointer; transition: background 0.12s; }
      .ed-row-tr:hover { background: #F8FAFC; }
      .ed-num { text-align: right; font-variant-numeric: tabular-nums; }
      .ed-section-h { font-size: 13px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.06em; margin: 18px 0 8px; }

      /* Cards de suscripciones B2B */
      .ed-sub-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 12px; margin-bottom: 16px; }
      .ed-sub-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 14px 16px; transition: all 0.15s; }
      .ed-sub-card.clickable { cursor: pointer; }
      .ed-sub-card.clickable:hover { border-color: #00A8B5; box-shadow: 0 2px 8px rgba(0,168,181,0.12); transform: translateY(-1px); }
      .ed-sub-card.historico { opacity: 0.65; background: #F8FAFC; }
      .ed-sub-card-ver { margin-left: auto; font-size: 11px; color: #00A8B5; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
      .ed-sub-card-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px; gap: 8px; }
      .ed-sub-card-head strong { color: #1A3461; font-size: 14.5px; }
      .ed-sub-precio { color: #00A8B5; font-weight: 800; font-size: 15px; font-variant-numeric: tabular-nums; }
      .ed-sub-precio small { font-size: 11px; color: #64748B; font-weight: 600; }
      .ed-sub-card-dir { font-size: 12.5px; color: #475569; margin: 4px 0; line-height: 1.4; }
      .ed-sub-card-meta { font-size: 12px; color: #64748B; margin: 4px 0; }
      .ed-sub-card-pausada { background: #FEF3C7; color: #78350F; padding: 6px 10px; border-radius: 6px; font-size: 11.5px; margin-top: 8px; }
      .ed-sub-card-cancelada { background: #FEE2E2; color: #991B1B; padding: 6px 10px; border-radius: 6px; font-size: 11.5px; margin-top: 8px; }
      .ed-sub-card-actions { display: flex; gap: 6px; margin-top: 10px; padding-top: 10px; border-top: 1px dashed #E2E8F0; }

      /* Tab Facturas */
      .ed-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
      .ed-fact-resumen { font-size: 13px; color: #475569; }
      .ed-fact-resumen strong { color: #1A3461; }
      .ed-fact-anulada { opacity: 0.55; }
      .ed-fact-anulada strong { text-decoration: line-through; }
      .ed-fact-actions { display: flex; gap: 4px; flex-wrap: wrap; }
      .ed-fact-actual { font-size: 11.5px; color: #00A8B5; margin-top: 4px; font-style: italic; }
      .ed-pill-muted { background: #F1F5F9; color: #64748B; font-size: 9.5px; padding: 1px 6px; border-radius: 999px; font-weight: 700; margin-left: 6px; text-transform: uppercase; letter-spacing: 0.05em; }

      /* Mapa de dirección */
      .ed-mapa-block { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; padding: 12px 14px; margin: 14px 0; }
      .ed-mapa-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
      .ed-mapa-head strong { font-size: 12.5px; color: #1A3461; }
      .ed-mapa-msg { padding: 7px 10px; border-radius: 6px; font-size: 12px; margin-bottom: 8px; }
      .ed-mapa-msg-ok { background: #DCFCE7; color: #14532D; }
      .ed-mapa-msg-warn { background: #FEF3C7; color: #78350F; }
      .ed-mapa-msg-error { background: #FEE2E2; color: #991B1B; }
      .ed-mapa-empty { padding: 18px; text-align: center; background: #fff; border: 1px dashed #E2E8F0; border-radius: 8px; color: #64748B; font-size: 12.5px; }
      .ed-mapa-coords { font-size: 11px; color: #64748B; margin-top: 6px; }
      .ed-mapa-coords code { font-family: 'SF Mono', monospace; background: #fff; padding: 2px 6px; border-radius: 4px; }

      .ed-pill-geo-ok { color: #16A34A; font-size: 13px; margin-left: 6px; cursor: help; }
      .ed-pill-geo-miss { background: #FEF3C7; color: #78350F; font-size: 9.5px; padding: 1px 6px; border-radius: 999px; font-weight: 700; margin-left: 6px; text-transform: uppercase; letter-spacing: 0.05em; }

      /* Fuera de zona de reparto (direcciones de empresa) */
      .ed-pill-fz { background: #EDE9FE; color: #5B21B6; font-size: 11px; padding: 2px 8px; border-radius: 999px; font-weight: 700; cursor: help; }
      .ed-pill-sin-barrio { background: #FEF3C7; color: #92400E; font-size: 11px; padding: 2px 8px; border-radius: 999px; font-weight: 700; cursor: help; }
      .ed-fz-check { display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 12.5px; color: #475569; cursor: pointer; user-select: none; }
      .ed-fz-check input { width: 16px; height: 16px; accent-color: #5B21B6; cursor: pointer; flex-shrink: 0; }
      .ed-fz-nota { margin-top: 6px; font-size: 11.5px; color: #5B21B6; background: #F5F3FF; border: 1px solid #DDD6FE; border-radius: 8px; padding: 8px 10px; line-height: 1.45; }
      .ed-fz-warn { margin-top: 6px; font-size: 11.5px; color: #92400E; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 8px; padding: 8px 10px; line-height: 1.45; }

      /* Timeline */
      .ed-timeline { list-style: none; padding: 0; margin: 0; }
      .ed-tl-item { display: grid; grid-template-columns: 140px 1fr; gap: 16px; padding: 14px 0; border-bottom: 1px solid #F1F5F9; }
      .ed-tl-item:last-child { border-bottom: none; }
      .ed-tl-tipo { font-size: 13px; font-weight: 700; color: #1A3461; }
      .ed-tl-fecha { font-size: 11.5px; color: #64748B; margin-bottom: 4px; }
      .ed-tl-resumen { font-size: 13.5px; color: #1A3461; font-weight: 600; }
      .ed-tl-notas { font-size: 13px; color: #475569; margin-top: 4px; line-height: 1.5; }
      .ed-tl-prox { background: #FEF3C7; padding: 6px 10px; border-radius: 6px; margin-top: 8px; font-size: 12.5px; color: #78350F; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
      .ed-tl-prox.done { background: #DCFCE7; color: #14532D; text-decoration: line-through; }
      .ed-tl-prox-label { font-weight: 700; }
      .ed-tl-prox-fecha { color: #92400E; }

      /* Modal (heredando estilos de Empresas) */
      .emp-modal-bg { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 16px; }
      .emp-modal { background: #fff; border-radius: 14px; max-width: 640px; width: 100%; max-height: 90vh; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 20px 40px rgba(15,23,42,0.2); }
      .emp-modal-head { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #E2E8F0; }
      .emp-modal-head h3 { margin: 0; font-size: 17px; font-weight: 800; color: #1A3461; }
      .emp-modal-close { background: transparent; border: none; font-size: 18px; cursor: pointer; color: #64748B; padding: 4px 8px; }
      .emp-modal-body { padding: 18px 20px; overflow-y: auto; flex: 1; }
      .emp-modal-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 14px 20px; border-top: 1px solid #E2E8F0; background: #F8FAFC; }
      .emp-btn-primary { background: #1A3461; color: #fff; padding: 9px 14px; border: none; border-radius: 8px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit; }
      .emp-btn-secondary { background: #fff; color: #475569; border: 1.5px solid #E2E8F0; padding: 9px 14px; border-radius: 8px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit; }
    `}</style>
  )
}
